import AdvancedSEOHead from "@/components/seo/AdvancedSEOHead";
import { SummaryBlock } from "@/components/summary/SummaryBlock";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Star, TrendingUp, Shield, Zap, Users } from "lucide-react";
import { useCta } from "@/hooks/useCta";

export default function AccompagnementAmazon() {
  const { handleCtaClick, resolveCta } = useCta();
  
  const summary = "Accompagnement Amazon complet : compte sain, Buy Box stable, contenus A+, Ads et opérations pilotées par la data.";
  
  const bullets = [
    "Audit d'entrée et plan 30 jours",
    "Gestion de compte Seller Central A→Z",
    "Buy Box : stratégie prix, stock, santé compte", 
    "Ads SP/SB/SD/DSP pilotées au ROAS/TACoS",
    "A+ & Store qui convertissent",
    "Reinstatement & conformité"
  ];

  const faqItems = [
    {
      question: "Qu'inclut l'accompagnement Amazon ?",
      answer: "Pilotage compte, Ads, contenus A+, santé compte et ops FBA/FBM avec reporting hebdo."
    },
    {
      question: "Combien de temps pour voir un effet ?",
      answer: "Dépend du point de départ ; le plan 30 jours couvre quick wins et hygiène de compte."
    },
    {
      question: "Travaillez-vous avec des industriels/DNVB ?",
      answer: "Oui, set-up ou reprise, avec objectifs clairs par SKU/gamme."
    },
    {
      question: "Pouvez-vous réactiver un ASIN/suspension ?",
      answer: "Oui, audit preuves et Plan d'Action jusqu'à réactivation."
    }
  ];

  const professionalServiceSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "serviceType": "Accompagnement Amazon (gestion de compte A→Z)",
    "name": "Accompagnement Amazon — Gestion de compte A→Z",
    "description": "Accompagnement Amazon complet : audit, gestion de compte, Buy Box, Ads SP/SB/SD/DSP, A+ & Stores, conformité et FBA/FBM",
    "provider": {
      "@type": "Organization",
      "name": "Cosmos Wave",
      "url": "https://www.cosmos-wave.com",
      "logo": "https://www.cosmos-wave.com/favicon.png"
    },
    "areaServed": ["FR", "EU"],
    "priceRange": "€€",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Services d'accompagnement Amazon",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Audit Amazon gratuit",
            "description": "Diagnostic complet de votre compte Amazon"
          }
        }
      ]
    }
  };

  const faqPageSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer", 
        "text": item.answer
      }
    }))
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Accueil",
        "item": "https://www.cosmos-wave.com/"
      },
      {
        "@type": "ListItem", 
        "position": 2,
        "name": "Services",
        "item": "https://www.cosmos-wave.com/services"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Accompagnement Amazon"
      }
    ]
  };

  return (
    <>
      <AdvancedSEOHead
        title="Agence d'accompagnement Amazon | Gestion de compte Amazon A→Z"
        description="Accompagnement Amazon : audit, gestion de compte, Buy Box, Ads SP/SB/SD/DSP, A+ & Stores, conformité et FBA/FBM. Audit gratuit."
        canonical="https://www.cosmos-wave.com/accompagnement-amazon"
        ogImage="/og/services-amazon-1200x630.jpg"
        ogType="website"
        keywords={["accompagnement Amazon", "agence d'accompagnement Amazon", "gestion compte Amazon", "accompagnement Seller Central"]}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(professionalServiceSchema) }}
      />
      <script
        type="application/ld+json" 
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqPageSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <main className="min-h-screen bg-gradient-to-b from-background to-muted/20">
        <div className="container mx-auto px-6 py-16">
          {/* Hero Section */}
          <section className="text-center mb-16">
            <Badge variant="outline" className="mb-4 bg-primary/10 text-primary border-primary/30">
              Accompagnement Amazon A→Z
            </Badge>
            <h1 className="text-4xl lg:text-5xl font-bold mb-6 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              Accompagnement Amazon — Gestion de compte A→Z
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto leading-relaxed">
              De l'audit initial à la croissance pérenne : pilotage complet de votre compte Amazon avec expertise SPN et méthode data-driven.
            </p>
            
            <SummaryBlock 
              summary={summary}
              bullets={bullets}
              defaultOpen={false}
              className="mb-8"
            />

            <div className="flex flex-wrap gap-4 justify-center">
              <Button 
                size="lg" 
                onClick={(e) => handleCtaClick('audit', e)}
                className="bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90"
              >
                <Star className="mr-2 h-5 w-5" />
                Demander un audit gratuit
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                onClick={(e) => handleCtaClick('whatsapp', e)}
              >
                Discuter sur WhatsApp
              </Button>
            </div>
          </section>

          {/* Ce que couvre l'accompagnement */}
          <section className="mb-16">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Ce que couvre l'accompagnement</h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                Une prise en charge complète de votre compte Amazon pour maximiser vos performances
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <Card className="border-primary/20 hover:border-primary/40 transition-colors">
                <CardContent className="p-6">
                  <Shield className="h-8 w-8 text-primary mb-4" />
                  <h3 className="font-semibold mb-3">Santé du compte</h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" />Audit complet de performance</li>
                    <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" />Résolution des suspensions</li>
                    <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" />Conformité ASIN & compte</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-primary/20 hover:border-primary/40 transition-colors">
                <CardContent className="p-6">
                  <TrendingUp className="h-8 w-8 text-primary mb-4" />
                  <h3 className="font-semibold mb-3">Buy Box & Pricing</h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" />Stratégie prix dynamique</li>
                    <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" />Optimisation Buy Box</li>
                    <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" />Gestion des stocks FBA/FBM</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-primary/20 hover:border-primary/40 transition-colors">
                <CardContent className="p-6">
                  <Zap className="h-8 w-8 text-primary mb-4" />
                  <h3 className="font-semibold mb-3">Amazon Ads</h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" />Campagnes SP/SB/SD/DSP</li>
                    <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" />Pilotage ROAS & TACoS</li>
                    <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" />Stratégie mots-clés avancée</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-primary/20 hover:border-primary/40 transition-colors">
                <CardContent className="p-6">
                  <Users className="h-8 w-8 text-primary mb-4" />
                  <h3 className="font-semibold mb-3">Contenus & Creative</h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" />A+ Content qui convertit</li>
                    <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" />Amazon Store design</li>
                    <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" />Optimisation listings SEO</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-primary/20 hover:border-primary/40 transition-colors">
                <CardContent className="p-6">
                  <Shield className="h-8 w-8 text-primary mb-4" />
                  <h3 className="font-semibold mb-3">Opérations & Logistique</h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" />Gestion FBA & FBM</li>
                    <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" />Expansion multi-pays EU</li>
                    <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" />Reporting & analytics</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-primary/20 hover:border-primary/40 transition-colors">
                <CardContent className="p-6">
                  <Star className="h-8 w-8 text-primary mb-4" />
                  <h3 className="font-semibold mb-3">Support & Formation</h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" />Support dédié quotidien</li>
                    <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" />Formation équipes internes</li>
                    <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" />Veille concurrentielle</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Méthode */}
          <section className="mb-16">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Méthode Data → Design → Delivery</h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                Notre approche structurée en 3 phases pour des résultats mesurables
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <Card className="bg-gradient-to-br from-primary/5 to-accent/5 border-primary/20">
                <CardContent className="p-6 text-center">
                  <div className="bg-primary/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-primary">1</span>
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Data</h3>
                  <p className="text-muted-foreground">Audit complet, analyse des KPIs et diagnostic de performance pour identifier les leviers de croissance.</p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-accent/5 to-primary/5 border-accent/20">
                <CardContent className="p-6 text-center">
                  <div className="bg-accent/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-accent">2</span>
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Design</h3>
                  <p className="text-muted-foreground">Stratégie personnalisée avec roadmap 30/90 jours et optimisations créatives qui convertissent.</p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-primary/5 to-accent/5 border-primary/20">
                <CardContent className="p-6 text-center">
                  <div className="bg-primary/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-primary">3</span>
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Delivery</h3>
                  <p className="text-muted-foreground">Exécution quotidienne, pilotage performance et reporting hebdomadaire avec optimisations continues.</p>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Mini cas clients */}
          <section className="mb-16">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Résultats clients</h2>
              <p className="text-muted-foreground text-lg">Quelques exemples de transformations réalisées</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <Card className="border-green-500/20">
                <CardContent className="p-6">
                  <Badge variant="outline" className="mb-3 bg-green-500/10 text-green-600 border-green-500/30">DNVB Beauté</Badge>
                  <h3 className="font-semibold mb-3">+275% chiffre d'affaires</h3>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Refonte A+ Content premium</li>
                    <li>• Campagnes DSP ciblées</li>
                    <li>• Buy Box stabilisée à 95%</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-blue-500/20">
                <CardContent className="p-6">
                  <Badge variant="outline" className="mb-3 bg-blue-500/10 text-blue-600 border-blue-500/30">Industriel B2B</Badge>
                  <h3 className="font-semibold mb-3">+150% volume ventes</h3>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Expansion 5 pays EU</li>
                    <li>• Catalogue 200+ références</li>
                    <li>• ROAS publicitaire 4.2x</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-purple-500/20">
                <CardContent className="p-6">
                  <Badge variant="outline" className="mb-3 bg-purple-500/10 text-purple-600 border-purple-500/30">Food Premium</Badge>
                  <h3 className="font-semibold mb-3">Réactivation complète</h3>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Compte suspendu récupéré</li>
                    <li>• 15 ASIN réactivés</li>
                    <li>• TACoS optimisé à 12%</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-16">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Questions fréquentes</h2>
            </div>

            <div className="max-w-3xl mx-auto space-y-6">
              {faqItems.map((item, index) => (
                <Card key={index} className="border-muted">
                  <CardContent className="p-6">
                    <h3 className="font-semibold mb-2">{item.question}</h3>
                    <p className="text-muted-foreground">{item.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* CTA Final */}
          <section className="text-center py-16">
            <Card className="bg-gradient-to-r from-primary/5 to-accent/5 border-primary/20">
              <CardContent className="p-12">
                <h2 className="text-3xl font-bold mb-4">Prêt à développer votre Amazon ?</h2>
                <p className="text-muted-foreground mb-8 text-lg max-w-2xl mx-auto">
                  Audit gratuit de votre compte Amazon et plan d'action personnalisé en 20-30 minutes.
                </p>
                <div className="flex flex-wrap gap-4 justify-center">
                  <Button 
                    size="lg" 
                    onClick={(e) => handleCtaClick('audit', e)}
                    className="bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90"
                  >
                    <Star className="mr-2 h-5 w-5" />
                    Demander votre audit gratuit
                  </Button>
                  <Button 
                    variant="outline" 
                    size="lg"
                    onClick={(e) => handleCtaClick('whatsapp', e)}
                  >
                    Discuter sur WhatsApp
                  </Button>
                </div>
              </CardContent>
            </Card>
          </section>
        </div>
      </main>
    </>
  );
}